import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductDto } from '@fewerbags/schema';
import type { storeProductShelfLifeDtoSchema } from '@fewerbags/schema';
import type { z } from 'zod';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Field } from '../Field';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { storeQueryKeys } from '../../pages/stores/queryKeys';
import { ProductPicker } from './ProductPicker';

type StoreProductShelfLifeDto = z.infer<typeof storeProductShelfLifeDtoSchema>;

/** Per-product shelf-life overrides (D6, the most specific tier). */
export function ProductShelfLifeSection({ storeId }: { storeId: string }) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<ProductDto | null>(null);
  const [days, setDays] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const shelfLife = useQuery({
    queryKey: storeQueryKeys.shelfLife(storeId),
    queryFn: () => api.stores.shelfLife.list(storeId),
  });

  const upsert = useMutation({
    mutationFn: (input: { productId: string; days: number; note: string | null }) =>
      api.stores.shelfLife.upsertProduct(storeId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: storeQueryKeys.shelfLife(storeId) });
      setPicked(null);
      setDays('');
      setNote('');
    },
  });

  const remove = useMutation({
    mutationFn: (productId: string) => api.stores.shelfLife.removeProduct(storeId, productId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: storeQueryKeys.shelfLife(storeId) });
    },
  });

  function submit() {
    setFormError(null);
    if (!picked) {
      setFormError('Search for a product and pick one.');
      return;
    }
    const daysNum = Math.round(Number(days));
    if (!Number.isFinite(daysNum) || daysNum < 0) {
      setFormError('Enter a shelf life of 0 or more days.');
      return;
    }
    upsert.mutate({ productId: picked.id, days: daysNum, note: note.trim() ? note.trim() : null });
  }

  const products: StoreProductShelfLifeDto[] = shelfLife.data?.products ?? [];

  return (
    <div className="flex flex-col gap-4">
      {shelfLife.isError ? <Alert tone="error">{messageForError(shelfLife.error)}</Alert> : null}
      {remove.isError ? <Alert tone="error">{messageForError(remove.error)}</Alert> : null}
      {products.length === 0 ? (
        <p className="text-sm text-muted">No product-specific overrides yet.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line rounded-lg border border-line">
          {products.map((p) => (
            <li key={p.productId} className="flex items-center justify-between gap-2 px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">{p.label}</p>
                {p.note ? <p className="truncate text-xs text-muted">{p.note}</p> : null}
              </div>
              <span className="whitespace-nowrap text-sm text-muted">{p.days} days</span>
              <button
                type="button"
                onClick={() => remove.mutate(p.productId)}
                disabled={remove.isPending}
                className="text-sm text-muted underline underline-offset-2 hover:text-ink"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-2 rounded-lg border border-dashed border-line p-3">
        <p className="text-sm font-medium text-ink">Add or update an override</p>
        <ProductPicker
          id="shelflife-product-picker"
          label="Product"
          onSelect={(p) => setPicked(p)}
        />
        {picked ? (
          <p className="text-xs text-muted">
            Selected: <span className="font-medium text-ink">{picked.label}</span>{' '}
            <button
              type="button"
              onClick={() => setPicked(null)}
              className="underline underline-offset-2"
            >
              change
            </button>
          </p>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          <Field
            id="shelflife-product-days"
            label="Shelf life (days)"
            type="number"
            inputMode="numeric"
            min={0}
            max={3650}
            value={days}
            onChange={(e) => setDays(e.target.value)}
          />
          <Field
            id="shelflife-product-note"
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        {formError ? <Alert tone="error">{formError}</Alert> : null}
        {upsert.isError ? <Alert tone="error">{messageForError(upsert.error)}</Alert> : null}
        <Button variant="secondary" onClick={submit} disabled={upsert.isPending}>
          {upsert.isPending ? 'Saving…' : 'Save override'}
        </Button>
      </div>
    </div>
  );
}
